import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { Pause, Play, PlayButton } from '~/ui/buttons';
import { select } from '~/lib/select';
import { shallow } from 'zustand/shallow';
import {
  MediaPlayerProvider,
  useMediaPlayerStore
} from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { AudioProgress } from '../audio-popup/components/AudioProgress';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import {
  GlobalMediaStore,
  useGlobalMediaStore
} from '~/ui/video-player/GlobalMediaProvider';
import { HORIZONTAL_AUDIO_PLAYER } from '../itemIds';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useEditorStore } from '~/shell/Store';
import NarrationPopup from '~/editor/narration/NarrationPopup';
import CheckIcon from '~/assets/icons/check.svg';

export type HorizontalAudioPlayerVariant = 'default' | 'compact' | 'card';

// Card variant follows the Figma "CAT Audio" component. Primary colour uses the
// app/brand accent token (not the literal purple from the design file).
export const CARD_COLORS = {
  surface: '#FFFFFF',
  text: '#6F6F6F'
};

interface Props {
  /** Audio record ID when the player uses a separate UI identifier. */
  audioId?: string;
  mediaUrl?: string;
  name?: string;
  body?: string;
  subtitlesUrl?: string;
  autoPlay?: boolean;
  blocking?: boolean;
  isolated?: boolean;
  replay?: boolean;
  parentId?: string;
  generated?: boolean;
  editable?: boolean;
  variant?: HorizontalAudioPlayerVariant;
  /**
   * `card` variant only. Padding of the player container. Defaults to '16px'.
   */
  padding?: string;
  /**
   * `card` variant only. When true (default) the player breaks out of the
   * surrounding LayoutList padding to span the full card width/bottom. Set to
   * false when the player is not inside a padded card (e.g. SocialMedia6/7).
   */
  fullBleed?: boolean;
  backgroundColor?: string;
}

const selector = (s: MediaPlayerStore) =>
  select(s, 'loadMedia', 'status', 'play', 'pause', 'progress', 'currentTime');

const playerSelector = (s: MediaPlayerStore) => s.player();

const seqSelector = (s: SequenceStore) =>
  select(s, 'setAudioPlayed', 'addBlocker', 'removeBlocker');

const CompactPlayButton = styled.button`
  background: ${p => p.theme.colors.accentDark};
  width: 38px;
  height: 38px;
  min-width: 38px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:focus {
    outline: 3px solid var(--color-accent-dk);
    outline-offset: 3px;
  }

  svg {
    fill: #fff;
  }

  #playBtnSvg {
    /* margin-left: 3px; */
  }
`;

export const CardPlayButton = styled.button`
  background: ${p => p.theme.colors.accentDark};
  width: 38px;
  height: 38px;
  min-width: 38px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:focus {
    outline: 3px solid var(--color-accent-dk);
    outline-offset: 3px;
  }

  svg,
  svg path {
    fill: #fff;
  }

  #playBtnSvg {
    /* margin-left: 2px; */
  }
`;

export const CardAudioProgress = ({ w }: { w?: string }) => {
  const progress = useMediaPlayerStore(s => s.progress);
  const progressAsNumber = parseFloat(String(progress));

  return (
    <x.div
      w={w || '100%'}
      h="7px"
      borderRadius="90px"
      backgroundColor="greyLight"
      overflow="hidden"
    >
      <x.div
        style={{ width: `${Math.floor(progressAsNumber * 100)}%` }}
        h="100%"
        backgroundColor="accentDark"
        borderRadius="90px"
        transition="width ease-in-out 0.3s"
      />
    </x.div>
  );
};

export const formatAudioTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }

  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const CompactAudioProgress = ({ w }: { w?: string }) => {
  const progress = useMediaPlayerStore(s => s.progress);
  const progressAsNumber = parseFloat(String(progress));

  return (
    <x.div
      w={w || '100%'}
      h="4px"
      borderRadius="999px"
      backgroundColor="greyLight"
      overflow="hidden"
    >
      <x.div
        style={{ width: `${Math.floor(progressAsNumber * 100)}%` }}
        h="100%"
        backgroundColor="accentDark"
        borderRadius="999px"
        transition="width ease-in-out 0.3s"
      />
    </x.div>
  );
};

interface AudioPlayerProps {
  audioId?: string;
  id?: string;
  parentId?: string;
  mediaUrl: string;
  subtitlesUrl?: string;
  name: string;
  body: string;
  variant?: HorizontalAudioPlayerVariant;
  padding?: string;
  fullBleed?: boolean;
  backgroundColor?: string;
}

const HorizontalAudioPlayerElement = ({
  id,
  audioId,
  parentId,
  mediaUrl,
  subtitlesUrl,
  name,
  body,
  variant = 'card',
  padding,
  fullBleed = false,
  backgroundColor
}: AudioPlayerProps) => {
  const attachedAudioId = audioId ?? id;
  const [showAudioPopup, setShowAudioPopup] = useState(false);
  const [duration, setDuration] = useState(0);
  const { editorMode, updateFile } = useEditorStore();
  const { loadMedia, status, pause, play, currentTime } = useMediaPlayerStore(
    selector,
    shallow
  );
  const player = useMediaPlayerStore(playerSelector);
  const { addBlocker, removeBlocker, setAudioPlayed } = useSequenceStore(
    seqSelector,
    shallow
  );
  const [isPaused, setIsPaused] = useState(false);

  useGlobalMedia();

  const globalMedia = useGlobalMediaStore() as GlobalMediaStore;
  const globalPlayer = globalMedia?.player;

  const ref = useRef<HTMLDivElement>();

  useEffect(() => {
    if (mediaUrl) {
      loadMedia(mediaUrl, subtitlesUrl, 'audio', false, 0);
    }
  }, [mediaUrl]);

  useEffect(() => {
    if (!player) {
      setDuration(0);
      return;
    }

    const updateDuration = () => {
      const nextDuration = player.duration();
      if (Number.isFinite(nextDuration) && nextDuration > 0) {
        setDuration(nextDuration);
      }
    };

    player.on('loadedmetadata', updateDuration);
    player.on('durationchange', updateDuration);
    updateDuration();

    return () => {
      player.off('loadedmetadata', updateDuration);
      player.off('durationchange', updateDuration);
    };
  }, [player, mediaUrl]);

  useEffect(() => {
    if (status === 'done') {
      setAudioPlayed(mediaUrl);
      // Quiz answer buttons / close-card are gated on `audioPlayed[linkedAudioId]`
      // where `linkedAudioId` is the audio id. Mark it by id too so those unblock.
      if (id) {
        setAudioPlayed(id);
      }
    }
  }, [status]);

  useEffect(() => {
    if (globalPlayer) {
      globalPlayer.pause();
    }
  }, []);

  useEffect(() => {
    if (status != 'done' && isPaused) {
      pause();
    }
  }, [status, open]);

  useEffect(() => {
    addBlocker(mediaUrl);

    if (status == 'nomedia') {
      removeBlocker(mediaUrl);
    }
  }, [status]);

  useEffect(() => {
    if ((mediaUrl && status === 'done') || !mediaUrl) {
      removeBlocker(mediaUrl);
    }
  }, [status, open, mediaUrl]);

  const onSelectNarration = (audio: {
    id?: number | string;
    mediaUrl?: string;
    [key: string]: unknown;
  }) => {
    if (attachedAudioId && audio?.id) {
      // Pass parentId so the audio is assigned to the specific owning card.
      // Without it, updateFile falls back to matching by audio id and targets
      // the first card sharing that audio.
      updateFile(
        attachedAudioId.toString(),
        audio as { id: number; mediaUrl: string },
        'audios',
        parentId
      );
    }
    setShowAudioPopup(false);
  };

  const clickHandler = () => {
    if (editorMode) {
      setShowAudioPopup(true);
      return;
    }

    if (globalPlayer) {
      setIsPaused(true);
      globalPlayer.pause();
      return;
    }

    setIsPaused(false);
    play();
  };

  const isFilename = (str: string) =>
    /\.(mp3|wav|m4a|ogg|flac)$/i.test(str?.trim() || '');

  const getContentForPlayer = (displayName: string, displayBody: string) => {
    if (displayName && displayBody == '') {
      return <x.span gridArea="title">{displayName}</x.span>;
    }
    return (
      <x.span gridArea="title">
        <x.h3 mb="15px">{displayName}</x.h3>
        <div dangerouslySetInnerHTML={{ __html: displayBody }} />
      </x.span>
    );
  };

  const showAddAudioPlaceholder = editorMode && !mediaUrl;
  const displayName = name && isFilename(name) ? name : 'AudioRecording01.wav';
  const playState = status === 'playing' ? 'playing' : 'paused';
  const cardPlayState: 'playing' | 'paused' | 'done' =
    status === 'playing' ? 'playing' : status === 'done' ? 'done' : 'paused';

  const narrationPopup =
    showAudioPopup &&
    editorMode &&
    ReactDOM.createPortal(
      <NarrationPopup
        onClose={() => setShowAudioPopup(false)}
        skipUpdate={true}
        onSelectCallback={onSelectNarration}
        id={attachedAudioId}
        skipSequenceNarration={true}
        subitemId={parentId}
        clearSubitemAudio={!!parentId}
      />,
      document.body
    );

  if (variant === 'card') {
    const fullBleedStyle = { width: '100%', padding: padding ?? '16px' };

    return (
      <x.div
        className="horizontalAudioCardPlayer"
        p={padding ?? '16px'}
        display="flex"
        flexDirection="column"
        gap="8px"
        alignItems="center"
        backgroundColor={backgroundColor}
        style={{
          ...fullBleedStyle,
          // A full-bleed player pins to the card bottom so its top padding
          // collapses to 0; a non-full-bleed player keeps the uniform `padding`
          // on all sides.
          ...(fullBleed ? { paddingTop: '0px' } : {})
        }}
        onClick={e => e.stopPropagation()}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={ref as any}
      >
        {showAddAudioPlaceholder ? (
          <x.div
            p="12px 20px"
            border="2px dashed"
            borderColor="accentDark"
            borderRadius="8px"
            cursor="pointer"
            color="accentDark"
            fontWeight={600}
            w="100%"
            textAlign="center"
            onClick={clickHandler}
          >
            Add Audio
          </x.div>
        ) : (
          mediaUrl && (
            <x.div
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="8px"
              w="100%"
            >
              <AudioPlayer />
              <CardPlayButton
                type="button"
                onClick={clickHandler}
                aria-label={
                  cardPlayState === 'playing'
                    ? 'Pause'
                    : cardPlayState === 'done'
                      ? 'Replay'
                      : 'Play'
                }
              >
                {cardPlayState === 'playing' ? (
                  <Pause />
                ) : cardPlayState === 'done' ? (
                  <CheckIcon />
                ) : (
                  <Play id="playBtnSvg" />
                )}
              </CardPlayButton>
              <x.div
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                gap="4px"
                flex="1"
                minWidth="0"
              >
                <x.div
                  fontWeight={600}
                  fontSize="14px"
                  lineHeight="20px"
                  style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    color: CARD_COLORS.text
                  }}
                >
                  {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
                </x.div>
                <CardAudioProgress w="100%" />
              </x.div>
            </x.div>
          )
        )}
        {narrationPopup}
      </x.div>
    );
  }

  if (variant === 'compact') {
    return (
      <x.div
        w="100%"
        p="md"
        onClick={e => e.stopPropagation()}
        display="flex"
        alignItems="center"
        gap="md"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={ref as any}
      >
        {showAddAudioPlaceholder ? (
          <x.div
            p="12px 20px"
            border="2px dashed"
            borderColor="accentDark"
            borderRadius="8px"
            cursor="pointer"
            color="accentDark"
            fontWeight={600}
            w="100%"
            textAlign="center"
            onClick={clickHandler}
          >
            Add Audio
          </x.div>
        ) : (
          mediaUrl && (
            <>
              <AudioPlayer />
              <CompactPlayButton
                type="button"
                onClick={clickHandler}
                aria-label={playState === 'playing' ? 'Pause' : 'Play'}
              >
                {playState === 'playing' ? <Pause /> : <Play />}
              </CompactPlayButton>
              <x.div flex="1" minWidth="0">
                <x.div
                  textAlign="right"
                  fontSize="sm"
                  color="greyDark"
                  mb="xs"
                  lineHeight="1"
                >
                  {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
                </x.div>
                <CompactAudioProgress w="100%" />
              </x.div>
            </>
          )
        )}
        {narrationPopup}
      </x.div>
    );
  }

  return (
    <x.div
      w="100%"
      p="md"
      onClick={e => e.stopPropagation()}
      display="grid"
      gridTemplateColumns="auto 1fr"
      gridTemplateAreas="'button title' 'button progress'"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
    >
      {getContentForPlayer(displayName, body)}
      {showAddAudioPlaceholder ? (
        <x.div
          mr="15px"
          gridArea="button"
          p="12px 20px"
          border="2px dashed"
          borderColor="accentDark"
          borderRadius="8px"
          cursor="pointer"
          color="accentDark"
          fontWeight={600}
          onClick={clickHandler}
        >
          Add Audio
        </x.div>
      ) : (
        mediaUrl && (
          <>
            <AudioPlayer />
            <x.div mr="15px" gridArea="button">
              <PlayButton state={playState} onClick={() => clickHandler()} />
            </x.div>
            <x.div gridArea="progress">
              <AudioProgress w="100%" />
            </x.div>
          </>
        )
      )}
      {narrationPopup}
    </x.div>
  );
};

export type HorizontalAudioPlayerDefinition = ItemDefinition<
  typeof HORIZONTAL_AUDIO_PLAYER,
  Props
>;

export class HorizontalAudioPlayer extends Item<
  typeof HORIZONTAL_AUDIO_PLAYER,
  Props
> {
  protected render({
    mediaUrl,
    name,
    body,
    subtitlesUrl,
    id,
    parent,
    parentId,
    audioId,
    variant = 'card',
    padding,
    fullBleed,
    backgroundColor
  }: RenderProps<Props>) {
    return (
      <MediaPlayerProvider>
        <HorizontalAudioPlayerElement
          id={id}
          audioId={audioId}
          parentId={parentId ?? parent?.id}
          mediaUrl={mediaUrl ?? ''}
          subtitlesUrl={subtitlesUrl}
          name={name ?? ''}
          body={body ?? ''}
          variant={variant}
          padding={padding}
          fullBleed={fullBleed}
          backgroundColor={backgroundColor}
        />
        {ReactDOM.createPortal(
          <>
            <MediaCaptions />
          </>,
          document.getElementById('root')!
        )}
      </MediaPlayerProvider>
    );
  }
}

registry[HORIZONTAL_AUDIO_PLAYER] = HorizontalAudioPlayer;
